import { ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {Object} CartItem
 * @property {string} title
 * @property {number} variant_id
 * @property {number} quantity
 * @property {number} product_id
 * @property {number} index
 */

/**
 * @typedef {Object} Cart
 * @property {number} item_count
 * @property {CartItem[]} items
 */

/**
 * A custom element that manages automatic gift with purchase.
 * Monitors cart for specific trigger products and adds/removes gift items accordingly.
 */
class GiftWithPurchaseComponent extends HTMLElement {
  TRIGGER_VARIANT_IDS = ['50671043150104', '50729201762584'];
  
  GIFT_VARIANT_ID = '50730818568472';
  
  #isProcessing = false;
  
  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    
    // Check cart state on initial load
    this.#syncGiftItems();
  }
  
  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }
  
  /**
   * Handles cart update events
   * @param {CustomEvent} event - The cart update event
   */
  #handleCartUpdate = async (event) => {
    // Prevent processing our own cart updates to avoid infinite loops
    if (this.#isProcessing) return;
    
    // Small delay to ensure cart state is fully updated
    await new Promise(resolve => setTimeout(resolve, 100));
    
    await this.#syncGiftItems();
  };
  
  /**
   * Syncs gift items with trigger product quantities
   */
  async #syncGiftItems() {
    if (this.#isProcessing) return;
    
    this.#isProcessing = true;
    
    try {
      // Fetch current cart state
      const cart = await this.#getCart();
      
      // Calculate required quantities
      const triggerQuantity = this.#getTriggerProductQuantity(cart);
      const currentGiftQuantity = this.#getGiftQuantity(cart);
      
      // Calculate how many gifts we need to add or remove
      const quantityDifference = triggerQuantity - currentGiftQuantity;
      
      if (quantityDifference > 0) {
        // Need to add more gift items
        await this.#addGiftItems(quantityDifference);
      } else if (quantityDifference < 0) {
        // Need to remove gift items
        await this.#removeGiftItems(Math.abs(quantityDifference), cart);
      }
      
    } catch (error) {
      console.error('[Gift with Purchase] Error syncing gift items:', error);
    } finally {
      this.#isProcessing = false;
    }
  }
  
  /**
   * Gets the current cart state
   * @returns {Promise<Cart>} The cart object
   */
  async #getCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }
  
  /**
   * Calculates total quantity of trigger products in cart
   * @param {Cart} cart - The cart object
   * @returns {number} Total quantity of trigger products
   */
  #getTriggerProductQuantity(cart) {
    return cart.items
      .filter((item) => this.TRIGGER_VARIANT_IDS.includes(item.variant_id.toString()))
      .reduce((total, item) => total + item.quantity, 0);
  }
  
  /**
   * Calculates total quantity of gift items in cart
   * @param {Cart} cart - The cart object
   * @returns {number} Total quantity of gift items
   */
  #getGiftQuantity(cart) {
    return cart.items
      .filter((item) => item.variant_id.toString() === this.GIFT_VARIANT_ID)
      .reduce((total, item) => total + item.quantity, 0);
  }
  
  /**
   * Adds gift items to the cart
   * @param {number} quantity - Number of gift items to add
   */
  async #addGiftItems(quantity) {
    const formData = new FormData();
    formData.append('id', this.GIFT_VARIANT_ID);
    formData.append('quantity', quantity.toString());
    
    // Get section IDs for cart components to update
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }
    
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript', { body: formData }),
        headers: {
          ...fetchConfig('javascript', { body: formData }).headers,
          'Accept': 'application/json',
        },
      });
      
      const data = await response.json();
      
      // Check for errors - Shopify returns errors in different formats
      if (response.status !== 200 || data.status || data.errors || data.error) {
        console.error('[Gift with Purchase] Failed to add gift:', data.message || data.errors || data.error);
      } else {
        // Trigger a cart update event to refresh UI
        this.#refreshCart(data.sections);
      }
    } catch (error) {
      console.error('[Gift with Purchase] Error adding gift items:', error);
    }
  }
  
  /**
   * Removes gift items from the cart
   * @param {number} quantityToRemove - Number of gift items to remove
   * @param {Cart} cart - The current cart object
   */
  async #removeGiftItems(quantityToRemove, cart) {
    // Find all gift items in the cart
    const giftItems = cart.items
      .map((item, index) => ({ ...item, line: index + 1 }))
      .filter(item => item.variant_id.toString() === this.GIFT_VARIANT_ID);
    
    if (giftItems.length === 0) return;
    
    let remainingToRemove = quantityToRemove;
    
    // Get section IDs for updates
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    
    // Remove gift items (process in reverse to maintain line numbers)
    for (const giftItem of giftItems.reverse()) {
      if (remainingToRemove <= 0) break;
      
      const quantityToRemoveFromThisLine = Math.min(giftItem.quantity, remainingToRemove);
      const newQuantity = giftItem.quantity - quantityToRemoveFromThisLine;
      
      const body = JSON.stringify({
        line: giftItem.line,
        quantity: newQuantity,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });
      
      try {
        const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
        const data = await response.json();
        
        if (data.errors) {
          console.error('[Gift with Purchase] Failed to remove gift:', data.errors);
        } else {
          // Refresh cart UI with updated sections
          this.#refreshCart(data.sections);
        }
      } catch (error) {
        console.error('[Gift with Purchase] Error removing gift items:', error);
      }
      
      remainingToRemove -= quantityToRemoveFromThisLine;
    }
  }
  
  /**
   * Refreshes cart UI by updating section HTML
   * @param {Object} sections - Section HTML from cart API response
   */
  #refreshCart(sections) {
    if (!sections) return;
    
    // Trigger cart update event to refresh all cart components
    const event = new CustomEvent(ThemeEvents.cartUpdate, {
      bubbles: true,
      detail: {
        resource: {},
        sourceId: 'gift-with-purchase',
        data: {
          source: 'gift-with-purchase',
          sections: sections,
        }
      }
    });
    
    document.dispatchEvent(event);
  }
}

if (!customElements.get('gift-with-purchase')) {
  customElements.define('gift-with-purchase', GiftWithPurchaseComponent);
}

